import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import { PageHeader, Card, Button, Badge, LoadingState } from '../components/ui';
import accountService from '../services/accountService';
import accountLivenessService from '../services/accountLivenessService';
import { PlusIcon } from '@heroicons/react/24/outline';

const Accounts = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [roleError, setRoleError] = useState({});
  const queryClient = useQueryClient();
  
  const { data: accounts = [], isLoading, error } = useQuery(
    'accounts',
    accountService.getAccounts,
    {
      refetchInterval: 6000,
      keepPreviousData: true,
      onSuccess: (data) => {
        // Debug: log first account to see structure
        if (data && data.length > 0) {
          console.log('First account data:', data[0]);
          console.log('limittime field:', data[0].limittime, 'type:', typeof data[0].limittime);
        }
      }
    }
  );

  // Mutation for checking account status
  const checkAccountMutation = useMutation(
    (accountId) => accountLivenessService.checkAccount(accountId),
    {
      onSuccess: () => {
        // Refetch accounts to get updated status
        queryClient.invalidateQueries('accounts');
      },
      onError: (error) => {
        console.error('Error checking account status:', error);
      }
    }
  );

  const changeRoleMutation = useMutation(
    ({ accountId, role }) => accountService.changeAccountRole(accountId, role),
    {
      onMutate: ({ accountId }) => {
        setRoleError((current) => ({ ...current, [accountId]: '' }));
      },
      onSuccess: (_data, { accountId }) => {
        setRoleError((current) => ({ ...current, [accountId]: '' }));
        queryClient.invalidateQueries('accounts');
      },
      onError: (mutationError, { accountId }) => {
        setRoleError((current) => ({
          ...current,
          [accountId]: mutationError.response?.data?.message || mutationError.message,
        }));
      },
    }
  );
  
  // Filter accounts based on search term
  const filteredAccounts = accounts.filter(account =>
    account.phonenumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    account.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    account.full_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  // Get status badge color
  const getStatusBadge = (status) => {
    switch (status) {
      case 'on':
        return <Badge variant="green">Enabled</Badge>;
      case 'off':
        return <Badge variant="gray">Disabled</Badge>;
      case 'banned':
        return <Badge variant="red">Banned</Badge>;
      case 'pending':
        return <Badge variant="yellow">Pending</Badge>;
      default:
        return <Badge variant="gray">{status}</Badge>;
    }
  };

  // Get live status badge color
  const getLiveStatusBadge = (liveStatus) => {
    switch (liveStatus) {
      case 'online':
        return <Badge variant="green">Online</Badge>;
      case 'offline':
        return <Badge variant="red">Offline</Badge>;
      case 'checking':
        return <Badge variant="yellow">Checking</Badge>;
      case 'error':
        return <Badge variant="red">Error</Badge>;
      case 'unknown':
      default:
        return <Badge variant="gray">Unknown</Badge>;
    }
  };

  const getAgentProvisioningLabel = (account) => {
    switch (account.agent_provisioning_status) {
      case 'pending':
        return 'Agent setup: waiting for first authorization';
      case 'running':
        return 'Agent setup: leaving existing groups and channels…';
      case 'completed':
        return `Agent setup complete · left ${account.agent_provisioning_left || 0}`;
      case 'completed_with_errors':
        return `Agent setup complete · left ${account.agent_provisioning_left || 0}, failed ${account.agent_provisioning_failed || 0}`;
      case 'failed':
        return 'Agent setup failed; account is blocked from Messenger join';
      default:
        return '';
    }
  };

  // Format rate limit display
  const formatRateLimit = (limittime) => {
    // Debug: log the limittime value
    if (limittime && limittime !== 0 && limittime !== '0') {
      console.log('limittime raw:', limittime, 'type:', typeof limittime);
    }

    if (!limittime || limittime === 0 || limittime === '0') {
      return <span className="text-sm text-gray-500">No Limit</span>;
    }

    // Convert to number if it's a string
    let limittimeNum = typeof limittime === 'string' ? parseInt(limittime, 10) : limittime;

    // Check if timestamp is in seconds (Unix timestamp < year 2286)
    // If it's less than 10 billion, it's likely in seconds, not milliseconds
    if (limittimeNum < 10000000000) {
      limittimeNum = limittimeNum * 1000; // Convert to milliseconds
    }

    const limitDate = new Date(limittimeNum);
    const now = new Date();

    console.log('limitDate:', limitDate.toLocaleString(), 'now:', now.toLocaleString(), 'is future:', limitDate > now);

    // Check if date is valid
    if (isNaN(limitDate.getTime())) {
      return <span className="text-sm text-gray-500">Invalid Date</span>;
    }

    if (limitDate <= now) {
      return <span className="text-sm text-gray-500">No Limit</span>;
    }

    return (
      <span className="text-sm text-red-600 font-medium">
        {limitDate.toLocaleString()}
      </span>
    );
  };

  const formatDateTime = (value) => {
    if (!value) {
      return '—';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '—';
    }
    return date.toLocaleString();
  };

  const formatRelativeTime = (value) => {
    if (!value) {
      return '—';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '—';
    }

    const seconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
    if (seconds < 60) {
      return `${seconds}s ago`;
    }
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) {
      return `${minutes}m ago`;
    }
    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
      return `${hours}h ago`;
    }
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  const getAuthorizationBadge = (state) => {
    switch (state) {
      case 'authorized':
        return <Badge variant="green">Authorized</Badge>;
      case 'waiting_for_code':
        return <Badge variant="yellow">Code Pending</Badge>;
      case 'waiting_for_password':
        return <Badge variant="yellow">2FA Pending</Badge>;
      case 'code_verification_pending':
      case 'password_verification_pending':
      case 'authorizing':
        return <Badge variant="blue">In Progress</Badge>;
      case 'failed':
        return <Badge variant="red">Auth Failed</Badge>;
      case 'not_authorized':
        return <Badge variant="gray">Not Authorized</Badge>;
      default:
        return <Badge variant="gray">Unknown</Badge>;
    }
  };

  const getConnectionFreshness = (checkedAt) => {
    if (!checkedAt) {
      return { label: 'Not checked yet', className: 'text-red-600' };
    }

    const parsed = new Date(checkedAt);
    if (Number.isNaN(parsed.getTime())) {
      return { label: 'Unknown check time', className: 'text-red-600' };
    }

    const ageSeconds = Math.floor((Date.now() - parsed.getTime()) / 1000);
    if (ageSeconds < 30) {
      return { label: 'Fresh now', className: 'text-green-600' };
    }

    if (ageSeconds < 180) {
      return { label: `${Math.floor(ageSeconds / 60)}m ago`, className: 'text-yellow-600' };
    }

    return { label: `${Math.floor(ageSeconds / 60)}m ago`, className: 'text-red-600' };
  };

  const shouldShowConnectionIssue = (account) => {
    if (!account) {
      return false;
    }

    const isHealthyConnection = account.status === 'on' && account.live_status === 'online';
    if (isHealthyConnection) {
      return false;
    }

    return Boolean(account.connection_error);
  };
  
  if (isLoading) {
    return <LoadingState message="Loading accounts..." />;
  }
  
  if (error) {
    return (
      <div className="text-center text-red-600">
        <p>Error loading accounts: {error.message}</p>
      </div>
    );
  }
  
  return (
    <div>
      <PageHeader 
        title="Telegram Accounts" 
        description="Manage your Telegram bot accounts"
        actions={
          <Button
            to="/accounts/new"
            variant="primary"
            icon={PlusIcon}
          >
            Add Account
          </Button>
        }
      />
      
      <Card className="mb-6">
        <div className="flex flex-col sm:flex-row justify-between items-center mb-4">
          <div className="w-full sm:w-64 mb-4 sm:mb-0">
            <label htmlFor="search" className="sr-only">
              Search
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                </svg>
              </div>
              <input
                id="search"
                name="search"
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                placeholder="Search accounts"
                type="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          
          <div className="flex space-x-2">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
              Enabled: {accounts.filter(a => a.status === 'on').length}
            </span>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
              Total: {accounts.length}
            </span>
          </div>
        </div>

        <div className="mb-4 text-xs text-gray-500">
          Control = manual on/off switch. Connection = real TDLib runtime connectivity.
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Phone
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Messenger Role
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Control
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Connection Issue
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Connection
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Authorization
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Activity
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Groups
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Full
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rate Limit
                </th>
                <th scope="col" className="relative px-6 py-3">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredAccounts.length > 0 ? (
                filteredAccounts.map((account) => (
                  <tr key={account.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {account.phonenumber}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{account.full_name || account.name || '-'}</div>
                      {account.full_name && <div className="text-xs text-gray-500">#{account.id}</div>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      <select
                        aria-label={`Messenger role for ${account.full_name || account.name || account.phonenumber}`}
                        className="rounded-md border-gray-300 text-sm focus:border-primary-500 focus:ring-primary-500"
                        value={account.type || 'monitoring'}
                        onChange={(event) => changeRoleMutation.mutate({
                          accountId: account.id,
                          role: event.target.value,
                        })}
                        disabled={changeRoleMutation.isLoading && changeRoleMutation.variables?.accountId === account.id}
                      >
                        <option value="monitoring">Observer</option>
                        <option value="agent">Agent</option>
                        <option value="ai">AI</option>
                      </select>
                      {changeRoleMutation.isLoading && changeRoleMutation.variables?.accountId === account.id && (
                        <div className="mt-1 text-xs text-gray-500">Saving…</div>
                      )}
                      {roleError[account.id] && (
                        <div className="mt-1 max-w-xs text-xs text-red-600" title={roleError[account.id]}>
                          {roleError[account.id]}
                        </div>
                      )}
                      {getAgentProvisioningLabel(account) && (
                        <div className={`mt-1 max-w-xs text-xs ${account.agent_provisioning_status === 'failed' ? 'text-red-600' : 'text-gray-500'}`}>
                          {getAgentProvisioningLabel(account)}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(account.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 max-w-sm">
                      {shouldShowConnectionIssue(account) ? (
                        <div>
                          <span className="block truncate" title={account.connection_error}>
                            {account.connection_error}
                          </span>
                          <div className="text-xs text-gray-400 mt-1">
                            Attempts: {account.connection_attempts_failed ?? 0}
                            {account.connection_attempted_at ? ` · ${new Date(account.connection_attempted_at).toLocaleString()}` : ''}
                          </div>
                        </div>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {(() => {
                        const freshness = getConnectionFreshness(account.connection_last_checked_at);
                        return account.status === 'on' ? (
                          <div
                            onClick={() => checkAccountMutation.mutate(account.id)}
                            className="cursor-pointer hover:opacity-80 inline-block"
                            title="Click to check runtime connection"
                          >
                            {(checkAccountMutation.isLoading && checkAccountMutation.variables === account.id) ?
                              <Badge variant="yellow">Checking...</Badge> :
                              getLiveStatusBadge(account.live_status)
                            }
                            <div className={`text-xs mt-1 ${freshness.className}`}>
                              {freshness.label}
                            </div>
                          </div>
                        ) : (
                          <div title="Enable account to run connection checks">
                            {getLiveStatusBadge('offline')}
                            <div className="text-xs mt-1 text-gray-500">Disabled by control</div>
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 max-w-xs">
                      <div className="space-y-1">
                        {getAuthorizationBadge(account.authorization_state)}
                        {account.authorization_last_error && (
                          <div className="text-xs text-red-600 truncate" title={account.authorization_last_error}>
                            {account.authorization_last_error}
                          </div>
                        )}
                        {account.authorization_last_code_requested_at && (
                          <div className="text-xs text-gray-500">
                            Code requested: {formatDateTime(account.authorization_last_code_requested_at)}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 max-w-xs">
                      <div className="space-y-1">
                        <div title={formatDateTime(account.last_activity_at)}>
                          {formatRelativeTime(account.last_activity_at)}
                        </div>
                        <div className="text-xs text-gray-500">
                          Last online: {formatDateTime(account.last_online_at)}
                        </div>
                        {account.live_status === 'offline' && (
                          <div className="text-xs text-gray-500">
                            Offline since: {formatDateTime(account.offline_since)}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {account.group_count || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {(account.group_count || 0) >= 500 ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          Full
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Available
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {formatRateLimit(account.limittime)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <Link 
                        to={`/accounts/${account.id}`} 
                        className="text-primary-600 hover:text-primary-900"
                      >
                        Details
                      </Link>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="12" className="px-6 py-4 text-center text-sm text-gray-500">
                    No accounts found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default Accounts;
